import React from 'react';
import { Alert, Card, CardContent, Typography } from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import { alpha } from '@mui/material/styles';
import Div from '@jumbo/shared/Div';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import JumboTextField from '@jumbo/components/JumboFormik/JumboTextField';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import { defaultSwalPreferences } from 'app/utils/appHelpers';
import { ASSET_IMAGES } from 'app/utils/constants/paths';
import authServices from 'app/services/auth-services';
import Logo from 'app/shared/Logo';
import { getTenantBranding } from "app/utils/tenantBranding";

const validationSchema = yup.object({
  first_name: yup.string().max(100, 'Nombre demasiado largo'),
  last_name: yup.string().max(100, 'Apellido demasiado largo'),
  password: yup
    .string('Ingresa tu contraseña')
    .required('Debes ingresar una contraseña'),
  password_confirmation: yup
    .string()
    .required('Debes confirmar tu contraseña')
    .oneOf([yup.ref('password'), null], 'Las contraseñas deben ser iguales'),
});

const AcceptInvitation = () => {
  const Swal = useSwalWrapper();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation_token');
  const tenantBranding = getTenantBranding();

  const onAcceptInvitation = (values, { setSubmitting }) => {
    if (!invitationToken) {
      setSubmitting(false);
      return;
    }

    authServices.acceptInvitation({
      invitation_token: invitationToken,
      first_name: values.first_name?.trim() || null,
      last_name: values.last_name?.trim() || null,
      password: values.password,
      password_confirmation: values.password_confirmation,
    })
      .then(() => {
        Swal.mixin(defaultSwalPreferences(Swal)).fire({
          icon: 'success',
          title: 'Invitación aceptada. Ahora puedes iniciar sesión.',
          background: 'success',
        });
        navigate('/auth/login');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Div sx={{
      flex: 1,
      flexWrap: 'wrap',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: (theme) => theme.spacing(4),
    }}>
      <Logo sx={{ mb: 3 }} logo_url={tenantBranding.authLogoUrl} img_style={{ maxHeight: '48px' }} />
      <Card sx={{ maxWidth: '100%', width: 420, mb: 4 }}>
        <Div sx={{ position: 'relative', height: '200px' }}>
          <CardMedia
            component="img"
            alt="banner-1"
            height="200"
            image={`${ASSET_IMAGES}/static/banner-1.jpg`}
          />
          <Div sx={{
            flex: 1,
            inset: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: (theme) => alpha(theme.palette.common.black, 0.5),
            p: (theme) => theme.spacing(3),
          }}>
            <Typography
              variant="h2"
              sx={{
                color: 'common.white',
                fontSize: '1.5rem',
                mb: 0,
              }}
            >
              Aceptar invitación
            </Typography>
          </Div>
        </Div>
        <CardContent>
          {!invitationToken && (
            <Alert severity="error" sx={{ mb: 2 }}>
              El enlace de invitación no es válido o no contiene token.
            </Alert>
          )}

          <Formik
            validateOnChange
            initialValues={{
              first_name: '',
              last_name: '',
              password: '',
              password_confirmation: '',
            }}
            validationSchema={validationSchema}
            onSubmit={onAcceptInvitation}
          >
            {({ isSubmitting }) => (
              <Form style={{ textAlign: 'left' }} noValidate autoComplete="off">
                <Typography variant="body1" mb={2}>
                  Define tus datos y crea tu contraseña para activar la cuenta.
                </Typography>

                <Div sx={{ mb: 2 }}>
                  <JumboTextField
                    fullWidth
                    name="first_name"
                    label="Nombre"
                  />
                </Div>

                <Div sx={{ mb: 2 }}>
                  <JumboTextField
                    fullWidth
                    name="last_name"
                    label="Apellido"
                  />
                </Div>

                <Div sx={{ mb: 2 }}>
                  <JumboTextField
                    fullWidth
                    name="password"
                    label="Contraseña"
                    type="password"
                  />
                </Div>

                <Div sx={{ mb: 3 }}>
                  <JumboTextField
                    fullWidth
                    name="password_confirmation"
                    label="Confirmar contraseña"
                    type="password"
                  />
                </Div>

                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting}
                  disabled={!invitationToken}
                >
                  Aceptar invitación
                </LoadingButton>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Div>
  );
};

export default AcceptInvitation;
